import throttle from 'lodash/throttle';

const THROTTLE_DELAY = 50;
const LIGHT_LUMINANCE_THRESHOLD = 0.65;

/*
  Sticky header functionality, only shown when scrolling up. Solution is based on Shopify Dawn theme
  StickHeader element, see
  https://github.com/Shopify/dawn/blob/5cd1a859751a5fc00e4dd8cf048ee8a67c4bb356/sections/header.liquid#L298
 */

export default function init() {
  const target = document.querySelector<HTMLElement>('.js-sticky-header')!;

  // Set up header height and resize callbacks
  setHeaderHeight(target);
  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      setHeaderHeight(entry.target as HTMLElement);
    }
  });
  resizeObserver.observe(target);

  // Get initial header bounds
  let originalBounds: DOMRect | null = null;

  const observer = new IntersectionObserver((entries, observer) => {
    originalBounds = entries[0].intersectionRect;
    observer.disconnect();
    initScrollHandler();
    updateNavTheme(target);
  });
  observer.observe(target);

  let lastScrollY = window.scrollY;
  const mobileMenuTrigger = document.querySelector<HTMLElement>('[aria-controls="mobile-menu"]');

  function initScrollHandler() {
    window.addEventListener(
      'scroll',
      throttle(() => {
        const currentScrollY = window.scrollY;

        if (currentScrollY <= originalBounds!.top) {
          requestAnimationFrame(() => reset(target));
        } else {
          const wasAnimated = target.classList.contains('is-animated');
          // Enable transition before toggling visibility so hide/show can animate
          target.classList.add('is-sticky', 'is-animated');
          // Flush styles when first enabling transition so the next translate change animates
          if (!wasAnimated) {
            void target.offsetHeight;
          }

          if (currentScrollY > lastScrollY && currentScrollY > originalBounds!.bottom) {
            // If menu is open, skip hiding
            if (mobileMenuTrigger?.classList.contains('open')) {
              return;
            }

            requestAnimationFrame(() => hide(target));
          } else if (currentScrollY < lastScrollY && currentScrollY > originalBounds!.bottom) {
            requestAnimationFrame(() => show(target));
          } else {
            requestAnimationFrame(() => show(target));
          }
        }

        requestAnimationFrame(() => updateNavTheme(target));
        lastScrollY = currentScrollY;
      }, THROTTLE_DELAY),
    );

    window.addEventListener(
      'resize',
      throttle(() => updateNavTheme(target), THROTTLE_DELAY),
    );
  }
}

function setHeaderHeight(target: HTMLElement) {
  document.documentElement.style.setProperty('--header-height', `${target.offsetHeight}px`);
}

function hide(target: HTMLElement) {
  target.classList.add('is-hidden');
}

function show(target: HTMLElement) {
  target.classList.remove('is-hidden');
}

function reset(target: HTMLElement) {
  target.classList.remove('is-hidden', 'is-sticky', 'is-animated');
}

/**
 * Switches nav to light/dark text based on what's behind it.
 * Prefers `[data-nav-theme]` on ancestors, otherwise samples background luminance.
 */
function updateNavTheme(header: HTMLElement) {
  const bounds = header.getBoundingClientRect();
  const x = Math.min(Math.max(window.innerWidth / 2, 0), window.innerWidth - 1);
  const y = Math.min(Math.max(bounds.bottom + 2, 0), window.innerHeight - 1);

  // Temporarily ignore the header so elementFromPoint hits content underneath
  const previousPointerEvents = header.style.pointerEvents;
  header.style.pointerEvents = 'none';
  const el = document.elementFromPoint(x, y) as HTMLElement | null;
  header.style.pointerEvents = previousPointerEvents;

  const theme = resolveNavTheme(el);
  header.classList.toggle('is-on-light', theme === 'light');
  header.classList.toggle('is-on-dark', theme === 'dark');
}

function resolveNavTheme(el: HTMLElement | null): 'light' | 'dark' {
  let node: HTMLElement | null = el;

  while (node && node !== document.documentElement) {
    const explicit = node.dataset.navTheme;
    if (explicit === 'light' || explicit === 'dark') {
      return explicit;
    }

    const bg = getComputedStyle(node).backgroundColor;
    if (!isTransparent(bg)) {
      const rgb = parseRgb(bg);
      if (rgb) {
        return getLuminance(...rgb) >= LIGHT_LUMINANCE_THRESHOLD ? 'light' : 'dark';
      }
    }

    node = node.parentElement;
  }

  return 'dark';
}

function isTransparent(color: string): boolean {
  if (!color || color === 'transparent') {
    return true;
  }

  const match = color.match(
    /rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)/i,
  );
  if (!match) {
    return false;
  }

  return match[4] !== undefined && Number(match[4]) === 0;
}

function parseRgb(color: string): [number, number, number] | null {
  const match = color.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (!match) {
    return null;
  }

  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

function getLuminance(r: number, g: number, b: number): number {
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}
